import axios from "axios";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
} from "@intelligems/ig-types/functions/experiences/experience-action";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { TabEnumFe, TabType } from "@intelligems/ig-types/fe-only/experiences";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { getMutationErrorMessage } from "@src/queryClient";
import {
  initExperimentFromDto,
  setExperienceTab,
} from "@src/store/slices/experiences";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { setIgnoreUpdateDetected } from "../../store/slices/global";
import {
  experienceKeys,
  historyKeys,
  invalidateModuleExperienceListQueries,
  offerKeys,
  scheduleKeys,
  tinybirdEvents,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useResetExperience = (category: ExperienceCategory) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["resetExperience"],
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info(`Resetting ${officialCategoryName(category, true)}`);
        } else {
          const errorDetail = getMutationErrorMessage(error);
          toast.error(
            `Error resetting ${officialCategoryName(category, true)} ${errorDetail ? `: ${errorDetail}` : ""}`
          );
        }
      }
    },
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [{ scope: `experiences-list-${data?.experience?.category}` }],
      });
      invalidateModuleExperienceListQueries(queryClient);
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(payload.pathParams.experienceId),
      });
      await queryClient.invalidateQueries({
        queryKey: experienceKeys.one(payload.pathParams.experienceId),
      });
      void queryClient.invalidateQueries({
        queryKey: scheduleKeys.one(payload.pathParams.experienceId),
      });
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: offerKeys.experiencesList,
      });
      void queryClient.invalidateQueries({
        queryKey: tinybirdEvents.summaryMetrics,
      });
      if (data.experience?.category === "experiment") {
        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: (searchParams.get("tab") as TabType) || TabEnumFe.ANALYTICS,
          })
        );
        dispatch(setExperienceTab(TabEnumFe.ANALYTICS));
      }
    },
  });
};
